<template>
  <!-- CP-96: real ship facts only. No loading / error / "not available"
       chrome, no generic blurb, no cabin categories posing as amenities. -->
  <div v-if="hasProfile" class="ship-details-card card">
    <h2>Ship Information</h2>
    <div class="ship-content">
      <div class="ship-header">
        <h3>{{ profile.name }}</h3>
        <span v-if="profile.cruise_line" class="cruise-line-badge">{{ profile.cruise_line }}</span>
      </div>
      
      <div v-if="profile.description" class="ship-description">
        <p>{{ profile.description }}</p>
      </div>
      
      <div v-if="profile.hasSpecs" class="ship-specs">
        <h4>Specifications</h4>
        <div class="specs-grid">
          <div v-if="profile.specs.year_built" class="spec-item">
            <i class="fas fa-calendar"></i>
            <div>
              <span class="spec-label">Year Built</span>
              <span class="spec-value">{{ profile.specs.year_built }}</span>
            </div>
          </div>
          <div v-if="profile.specs.capacity" class="spec-item">
            <i class="fas fa-users"></i>
            <div>
              <span class="spec-label">Capacity</span>
              <span class="spec-value">{{ profile.specs.capacity.toLocaleString() }} passengers</span>
            </div>
          </div>
          <div v-if="profile.specs.tonnage" class="spec-item">
            <i class="fas fa-weight"></i>
            <div>
              <span class="spec-label">Tonnage</span>
              <span class="spec-value">{{ profile.specs.tonnage.toLocaleString() }} GT</span>
            </div>
          </div>
          <div v-if="profile.specs.length" class="spec-item">
            <i class="fas fa-ruler-horizontal"></i>
            <div>
              <span class="spec-label">Length</span>
              <span class="spec-value">{{ profile.specs.length }}m</span>
            </div>
          </div>
          <div v-if="profile.specs.decks" class="spec-item">
            <i class="fas fa-layer-group"></i>
            <div>
              <span class="spec-label">Decks</span>
              <span class="spec-value">{{ profile.specs.decks }}</span>
            </div>
          </div>
          <div v-if="profile.specs.crew" class="spec-item">
            <i class="fas fa-user-tie"></i>
            <div>
              <span class="spec-label">Crew</span>
              <span class="spec-value">{{ profile.specs.crew.toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="profile.amenities.length > 0" class="ship-amenities">
        <h4>Amenities</h4>
        <div class="amenities-grid">
          <div v-for="(amenity, index) in profile.amenities" :key="index" class="amenity-item">
            <i class="fas fa-check-circle"></i>
            <span>{{ amenity }}</span>
          </div>
        </div>
      </div>
      
      <div v-if="profile.deck_plan_url" class="deck-plan-section">
        <h4>Deck Plans</h4>
        <a :href="profile.deck_plan_url" target="_blank" rel="noopener" class="deck-plan-link">
          <i class="fas fa-map"></i>
          View Deck Plans
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { shipProfile, hasShipProfile } from '@/utils/detailModules';

export default {
  name: 'ShipDetails',
  props: {
    shipName: {
      type: String,
      required: false
    },
    shipInfo: {
      type: Object,
      required: false
    }
  },
  setup(props) {
    const profile = computed(() => shipProfile(props.shipInfo, props.shipName));
    const hasProfile = computed(() => hasShipProfile(props.shipInfo, props.shipName));

    return {
      profile,
      hasProfile
    };
  }
};
</script>

<style scoped>
.ship-details-card {
  margin-bottom: 24px;
}

.ship-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.ship-header h3 {
  margin: 0;
  font-size: 24px;
  color: var(--cs-d-text);
}

.cruise-line-badge {
  background-color: var(--cs-d-tint);
  color: var(--cs-d-link);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
}

.ship-description {
  margin-bottom: 24px;
  color: var(--cs-d-muted);
  line-height: 1.6;
}

.ship-specs {
  margin-bottom: 24px;
}

.ship-specs h4,
.ship-amenities h4,
.deck-plan-section h4 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: var(--cs-d-text);
  border-bottom: 2px solid var(--cs-d-divider);
  padding-bottom: 8px;
}

.specs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.spec-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
}

.spec-item i {
  font-size: 20px;
  color: var(--cs-d-link);
  width: 24px;
  text-align: center;
}

.spec-item div {
  display: flex;
  flex-direction: column;
}

.spec-label {
  font-size: 12px;
  color: var(--cs-d-muted);
  margin-bottom: 4px;
}

.spec-value {
  font-size: 16px;
  font-weight: 500;
  color: var(--cs-d-text);
}

.ship-amenities {
  margin-bottom: 24px;
}

.amenities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.amenity-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: var(--cs-d-surface-alt);
  border-radius: 6px;
}

.amenity-item i {
  color: var(--cs-d-success);
  font-size: 16px;
}

.deck-plan-section {
  margin-top: 24px;
}

.deck-plan-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: #1a73e8;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.deck-plan-link:hover {
  background-color: #1557b0;
}

@media (max-width: 768px) {
  .specs-grid {
    grid-template-columns: 1fr;
  }
  
  .amenities-grid {
    grid-template-columns: 1fr;
  }
}
</style>

